import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import {
  Box,
  Grid,
  Stack,
  Button,
  TextField,
  Typography,
  Alert,
  Autocomplete,
  Avatar,
  Chip,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";
import { Inventory2Outlined, ArticleOutlined, CreateOutlined, SaveOutlined, OpenInNew, Close } from '@mui/icons-material'
import LoadingOverlay from "../Common/LoadingOverlay";
import CustomButton from "../Common/CustomButton";
import DeleteDialog from "../Common/DeleteDialog";
import Status from "../Common/Status";

import { updateTestSuite, deleteTestSuite } from "../../redux-store/testSuiteReducers/testSuiteActions";

import "../../styles/components/Common/EditDetail.scss";

const EditDetail = ({ testSuiteId, attachedScenarios, setTestSuiteDetail, closeDrawer }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [detail, setDetail] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [errors, setErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState("");
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [tagText, setTagText] = useState(false);

  const testSuiteDetail = useSelector(state => state.testSuite.selectedTestSuiteDetail);
  const isLoading = useSelector((state) => state.testSuite.isLoading);
  const isSaving = useSelector((state) => state.testSuite.isSaving);
  const allTags = useSelector((state) => state.testSuite.testSuiteTags);
  const allOwners = useSelector((state) => state.user.owners);

  const allStatuses = ["draft", "active", "archived"];

  useEffect(() => {
    setInitialState();
  }, [testSuiteDetail]);

  const setInitialState = () => {
    const detailObj = {
      title: testSuiteDetail?.title || "",
      description: testSuiteDetail?.description || "",
      tags: testSuiteDetail?.tags || [],
      owner: testSuiteDetail?.owner || {},
      status: testSuiteDetail?.status || "draft",
    };
    setDetail(detailObj);
  };

  const updateTestScenarioDetail = async (data) => {
    await dispatch(updateTestSuite({ testSuiteId, data }));
    setIsEdit(false);
    triggerAlert("Test suite details updated successfully");
  };

  const triggerAlert = (msg) => {
    setAlertMessage(msg);
    const timeoutId = setTimeout(() => {
      setAlertMessage("");
      clearTimeout(timeoutId);
    }, 5000);
  };

  const onChange = (name, value) => {
    if (name === "title") updateErrors("title", "");
    setDetail((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const updateErrors = (key, msg = "") => {
    setErrors((prevState) => {
      const newState = { ...prevState };
      newState[key] = msg;
      if (!msg) delete newState[key];
      return newState;
    });
  };

  const onOwnerChange = (event, newValue) => {
    setDetail((prevState) => ({
      ...prevState,
      owner: newValue,
    }));
    // updateErrors("owner", "");
  };

  const onEdit = () => {
    setInitialState();
    setIsEdit(true);
  };

  const toggleArchiveModal = () => {
    setShowArchiveModal((prevState) => !prevState);
  };

  const onArchiveTestScenario = async () => {
    const response = await dispatch(
      updateTestSuite({
        testSuiteId,
        data: { status: "archived" },
      })
    );

    setAlertMessage("Test suite archived successfully.");
    toggleArchiveModal();
    if (response?.payload) setTestSuiteDetail(response?.payload)
  };

  const onDeleteTestScenario = async () => {
    await dispatch(deleteTestSuite({ testSuiteId }));
    setAlertMessage("Test suite deleted successfully.");
    closeDrawer();
    history.push("/test-suites");
  };

  const onSave = () => {
    const formattedDetail = {
      ...detail,
      owner_id: detail?.owner?.id,
    };
    let error = false;
    if (!formattedDetail?.owner_id) {
      updateErrors("owner", "Required");
      error = true;
    }
    if (!formattedDetail?.title || !formattedDetail.title.trim()) {
      updateErrors("title", "Required");
      error = true;
    }
    if (error) {
      return;
    }
    updateTestScenarioDetail(formattedDetail);
  };

  const onCancelEdit = () => {
    setIsEdit(false);
    setInitialState();
  };

  const handleDeleteTag = (newValue) => {
    setDetail((prevState) => ({
      ...prevState,
      tags: [...prevState.tags.filter((tag) => newValue !== tag)],
    }));
  };

  const onChangeTag = (event, newValue) => {
    if (newValue && newValue.trim() && detail?.tags?.indexOf(newValue) === -1) {
      setDetail((prevState) => ({
        ...prevState,
        tags: [...prevState.tags, newValue],
      }));
    } else {
      event.preventDefault();
    }
  };

  const onStatusChange = (event, newValue) => {
    setDetail((prevState) => ({
      ...prevState,
      status: newValue,
    }));
  };

  const getFilteredTags = () => {
    return allTags.filter((tag) => detail?.tags?.indexOf(tag) === -1);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && tagText && tagText.trim()) {
      event.preventDefault();
      event.stopPropagation();
      const trimmedTag = tagText.trim();
      const tagIndex = detail.tags.findIndex(
        (tag) => tag.toLowerCase() === trimmedTag.toLowerCase()
      );
      if (tagIndex >= 0) {
        return;
      }
      setDetail((prevState) => ({
        ...prevState,
        tags: [
          ...prevState.tags,
          trimmedTag[0].toUpperCase() + trimmedTag.slice(1),
        ],
      }));
      setTagText("");
    }
  };

  if (isLoading) {
    return <LoadingOverlay isLoading={isLoading} />;
  }

  let content = null;
  content = (
    <>
      <div className="section">
        <h6>ID</h6>
        {isEdit ? (
          <TextField
            sx={{ mb: 2, width: "100%" }}
            disabled
            size="small"
            variant="outlined"
            value={testSuiteDetail?.code}
          />
        ) : (
          <Typography>{testSuiteDetail?.code}</Typography>
        )}
      </div>
      <div className="section">
        <h6>Title</h6>
        {isEdit ? (
          <TextField
            sx={{ mb: 2, width: "100%" }}
            size="small"
            variant="outlined"
            value={detail?.title}
            onChange={(e) => {
              onChange("title", e.target.value);
            }}
          />
        ) : (
          <Typography>{testSuiteDetail?.title}</Typography>
        )}
        {errors?.title && (
          <Typography sx={{ color: "red" }}>{errors.title}</Typography>
        )}
      </div>
      <div className="section">
        <h6>Description</h6>
        {isEdit ? (
          <textarea
            style={{ marginBottom: 2, resize: "none", width: "100%" }}
            rows={3}
            size="small"
            variant="outlined"
            value={detail?.description}
            onChange={(e) => {
              onChange("description", e.target.value);
            }}
          />
        ) : (
          <div className="truncate-2-lines description" title={testSuiteDetail?.description}>{testSuiteDetail?.description}</div>
        )}
        {errors?.description && (
          <Typography sx={{ color: "red" }}>{errors.description}</Typography>
        )}
      </div>
      <div className="section">
        <h6>Status</h6>
        <RadioGroup
          aria-labelledby="status-radio-buttons-group-label"
          onChange={onStatusChange}
          value={isEdit ? detail.status : testSuiteDetail.status}
        >
          {!isEdit && (
            allStatuses.filter(
              (status) => status === testSuiteDetail.status
            )
            ).map((status) => (
              <Status status={status} />
            )
          )}
          {isEdit && allStatuses.map((status) => (
            <FormControlLabel
              key={status}
              sx={{ height: "30px" }}
              value={status}
              control={<Radio />}
              label={(<Status status={status} />)}
            />
          ))}
        </RadioGroup>
      </div>
      <div className="section">
        <h6>Owner</h6>
        {isEdit ? (
          <Autocomplete
            options={allOwners}
            getOptionLabel={(option) => option.name || option.email}
            onChange={onOwnerChange}
            value={detail?.owner}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{ mt: 1 }}
                size="small"
                variant="outlined"
              />
            )}
          />
        ) : (
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar
              alt={testSuiteDetail?.owner?.name}
              src={testSuiteDetail?.owner?.avatar}
              sx={{ width: 24, height: 24 }}
            />
            <Typography variant="body1">
              {testSuiteDetail?.owner?.name}
            </Typography>
          </Stack>
        )}
        {errors?.owner && (
          <Typography sx={{ color: "red" }}>{errors.owner}</Typography>
        )}
      </div>
      <div className="tagsContainer section">
        <h6>Tags</h6>
        {isEdit ? (
          <div className="tags">
            {detail?.tags?.map((tag) => (
              <Chip
                key={tag}
                sx={{ mr: 1, mt: 1 }}
                label={tag}
                onDelete={() => {
                  handleDeleteTag(tag);
                }}
              />
            ))}
            <Autocomplete
              options={getFilteredTags()}
              onChange={onChangeTag}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ mt: 1 }}
                  onChange={(e) => {
                    setTagText(e.target.value);
                  }}
                  value={tagText}
                  onKeyDown={handleKeyDown}
                  size="small"
                  variant="outlined"
                />
              )}
            />
          </div>
        ) : (
          <div>
            {testSuiteDetail?.tags?.map((tag) => (
              <Chip key={tag} sx={{ mr: 1, mt: 1 }} label={tag} />
            ))}
          </div>
        )}
      </div>
      {!isEdit && (
        <>
          <div className="section">
            <h6>Associated Test Plans</h6>
            {attachedScenarios?.length === 0 &&
              "No associated test plans"}
            {attachedScenarios?.map(
              (testScenario) => (
                <div key={testScenario.id}>
                  {testScenario.title}
                  <OpenInNew
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      window.open(
                        `${window.location.origin}/test-scenarios/${testScenario.id}`,
                        "_blank"
                      );
                    }}
                  />
                </div>
              )
            )}
          </div>
          <div className="section">
            <h6>Created At</h6>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Avatar
                alt={testSuiteDetail?.created_by?.name}
                src={testSuiteDetail?.created_by?.avatar}
                sx={{ width: 24, height: 24 }}
              />
              <Typography variant="body1">
                {testSuiteDetail?.created_by?.name}
              </Typography>
            </Stack>
            {testSuiteDetail?.created_at && (
              <Typography>
                {Date(testSuiteDetail?.created_at * 1000).toString()}
              </Typography>
            )}
          </div>
        </>
      )}
    </>
  );

  const nonEditModeActions = (
    <Grid container spacing={2} sx={{ flexDirection: "row-reverse" }}>
      <CustomButton
        variant="outlined"
        color="primary"
        disabled={false}
        onClick={onEdit}
      >
        <CreateOutlined sx={{ mr: 1 }} />
        Edit
      </CustomButton>
    </Grid>
  );

  const editModeActions = (
    <Grid container spacing={2}>
      <CustomButton
        variant="outlined"
        sx={{ mr: 2 }}
        color="primary"
        disabled={isSaving}
        onClick={onSave}
      >
        <SaveOutlined sx={{ mr: 1 }} />
        Save
      </CustomButton>
      <CustomButton
        variant="outlined"
        color="primary"
        disabled={isSaving}
        onClick={onCancelEdit}
      >
        <Close sx={{ mr: 1 }} />
        Cancel
      </CustomButton>
    </Grid>
  );

  return (
    <Box sx={{ p: 2, px: 4, color: "var(--text-primary)", backgroundColor: "var(--background-primary)" }} className="editDetail">
      {isSaving && <LoadingOverlay isLoading={isSaving} />}
      <Grid container spacing={2}>
        <Grid item xs={6} md={6}>
          {isEdit ? (
            <>
              <h5>
                <CreateOutlined sx={{ mr: 1 }} />
                Edit Basic Details
              </h5>
            </>
          ) : (
            <>
              <h5>
                <ArticleOutlined sx={{ mr: 1 }} />
                Basic Details
              </h5>
            </>
          )}
        </Grid>
        <Grid item xs={6} md={6}>
          {!isEdit && nonEditModeActions}
        </Grid>
        <Grid item xs={12} md={12}>
          {content}
        </Grid>
        <Grid item xs={12} md={12} style={{ padding: "32px 40px" }}>
          {isEdit && editModeActions}
        </Grid>
        {!isEdit && (
          <div style={{ borderBottom: "1px solid grey", width: "100%" }}></div>
        )}
        <Grid item xs={12} md={12}>
          {!isEdit && (
            <>
              <h5 style={{ color: "#d32f2f" }}>Danger Zone</h5>
              <Button
                variant="contained"
                sx={{ width: "100%" }}
                color="error"
                disabled={false}
                onClick={toggleArchiveModal}
              >
                Delete
              </Button>
            </>
          )}
        </Grid>
      </Grid>

      <DeleteDialog
        open={showArchiveModal}
        onClose={toggleArchiveModal}
        title="Delete Test Suite? Archive instead!"
        deleteBtnLabel="Delete Permanently"
        secondaryBtnLabel="Archive"
        secondaryBtnIcon={<Inventory2Outlined />}
        onDeleteAction={onDeleteTestScenario}
        onSecondaryAction={onArchiveTestScenario}
      >
        <Typography variant="body1" sx={{ marginBottom: '16px' }}>
          We recommend archiving Test Suites instead of deleting.
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: '16px' }}>
          Are you sure you want to permanently delete this Test Suite?
        </Typography>
      </DeleteDialog>

      {alertMessage && (
        <Alert sx={{ position: "fixed", top: 20, right: 20 }}>
          {alertMessage}
        </Alert>
      )}
    </Box>
  );
};

export default EditDetail;
